// The layer of controls over a GFM table: row and column handles (with their
// menus), the "+" strips that add a row or a column, and the column width
// grips. It measures nothing: positions come in `rects`, the text size in
// `fontSizePx` (the node view measures; split this way, every behaviour can be
// tested in happy-dom).
//
// Column widths are `widths` (em). In a table that fits the body, a grip
// between columns trades width with the next column; in a wider table only the
// grabbed column changes (`resizeColumn` in table-geometry.ts). The width is
// written once, on release.
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Selection } from "@tiptap/pm/state";
import { DEFAULT_COLUMN_EM } from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Icon } from "../../ui/icon";
import { selectCell, separateUndo } from "./editor-steps";
import { FloatingMenu, type FloatingMenuItem } from "./floating-menu";
import {
  columnEdgePx,
  displayedTotalPx,
  pxToEm,
  resizeColumn,
  widthsFromMeasured,
} from "./table-geometry";

/**
 * Rectangles to place the controls on (all `getBoundingClientRect()`). The
 * node view measures them and passes them in again whenever the table's size
 * changes (fonts arrive late); the overlay measuring by itself could not tell
 * how old its numbers are.
 */
export interface TableRects {
  /** Each row (`<tr>`). */
  rows: DOMRect[];
  /**
   * The header row's cells (= the columns). One cell = one column: GFM tables
   * have no merged cells. A table pasted as HTML with merged cells gets
   * handles and grips that do not line up with its columns.
   */
  cols: DOMRect[];
  /** The `<table>` itself: the origin of the overlay's coordinates. */
  table: DOMRect;
}

export interface TableOverlayProps {
  editor: Editor;
  /** The table node's position (undefined right after it went away). */
  getPos: () => number | undefined;
  node: PMNode;
  rects: TableRects;
  /** The table's font size (px), for px <-> em. */
  fontSizePx: number;
  /** The widths (em) on release. The node view writes them. */
  onResize: (widths: number[]) => void;
  /**
   * Widths (em) shown while dragging; null restores. Not written to the
   * document (no Yjs round trip per pixel): the node view puts them into the
   * colgroup.
   */
  onPreviewWidths?: (widths: number[] | null) => void;
  /**
   * How much the table shrank while dragging (px); null eases back to 0 over
   * 180ms. Shrinking a column while scrolled to the far right also shrinks the
   * scroll range, so the browser cut `scrollLeft` and the content slid right:
   * the grabbed border stayed at the right edge while the pointer moved left.
   * Filling what shrank with the container's `padding-right` keeps the range,
   * so the border follows the pointer. Restored on release.
   */
  onPreviewSlack?: (px: number | null) => void;
  /**
   * Scrolls the container while a grip is held: adds `dx` to `scrollLeft` and
   * returns the actual `scrollLeft` (so the caller sees when it hit an end).
   * `dx` 0 only reads. Widening to the right used to stop at the container's
   * edge; inside the edge band the container scrolls and the scrolled amount
   * adds to the width.
   */
  onAutoScroll?: (dx: number) => number;
  /**
   * Menu open or closed. The node view marks `div.trameria-table` with
   * `data-menu-open` so this table's overlay is raised while its menu is open:
   * the overlay has a z-index and so a stacking context, and with two tables
   * the later table's strips were drawn over the earlier table's menu.
   */
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * The container's scroll position (px). The overlay is the container's
   * sibling and does not scroll with it: content is placed from `rects`
   * (table based) minus `scroll`. Frozen rows and columns are not shifted, so
   * they stay put; handles that slid under them are hidden.
   */
  scroll?: { top: number; left: number };
  /**
   * The container's size: `client*` is the visible content without scrollbars
   * (the box that clips the strips and grips), `offset*` the outer size
   * (where the "+" strips go).
   */
  viewport?: {
    clientWidth: number;
    clientHeight: number;
    offsetWidth: number;
    offsetHeight: number;
  };
}

/** Sizes of the strips, handles and grips (px). */
const STRIP = 16;
const STRIP_GAP = 4;
const RIGHT_STRIP = 12;
const GRIP = 8;
/**
 * The handle band's thickness (px). It straddles the container's border: the
 * band is centred on the line (half of it, -6px) and the pill (10px) sits on
 * the same line, so it reads as belonging to that row or column.
 */
const BAND = 12;
const BAND_HALF = BAND / 2;
/**
 * While a grip is held, inside this band at either edge the container scrolls
 * (px; beyond the edge too). Per frame it moves half the overshoot, kept
 * within {@link AUTO_MIN}..{@link AUTO_MAX}.
 */
const AUTO_EDGE = 32;
const AUTO_MIN = 2;
const AUTO_MAX = 16;

type OpenMenu = { kind: "row" | "col"; index: number; anchor: DOMRect };

/** Stops the auto-scroll loop (module level so it is not a hook dependency). */
function cancelFrame(frame: { current: number | null }) {
  if (frame.current !== null && typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(frame.current);
  }
  frame.current = null;
}

/**
 * A "+" strip. `preventDefault` on mousedown keeps ProseMirror from moving the
 * selection and focus (the node view's `stopEvent` keeps the event from PM,
 * but the default action still runs). The icon is `pointer-events-none`, so
 * the target is always the button. How it shows (only when the pointer is on
 * the strip; never on touch devices) is in the CSS (`.trameria-table-add`).
 */
function AddStrip({
  label,
  style,
  onClick,
  iconSize = 13,
}: {
  label: string;
  style: CSSProperties;
  onClick: () => void;
  /** The "+" size (px). Wider than the strip, it sticks out 1px and adds a sideways scroll. */
  iconSize?: number;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className="trameria-table-add"
      style={style}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <Icon kind="plus" size={iconSize} />
    </button>
  );
}

/** Three dots in a handle (vertical for rows, horizontal for columns). */
function Dots({ direction }: { direction: "row" | "col" }) {
  const centers = [4, 8, 12];
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="pointer-events-none"
    >
      {centers.map((c) => (
        <circle
          key={c}
          cx={direction === "col" ? c : 8}
          cy={direction === "col" ? 8 : c}
          r="1.4"
          fill="currentColor"
        />
      ))}
    </svg>
  );
}

/**
 * A row or column handle (placed as Notion does): the pill's centre sits on the
 * table's border - a column's on the top edge, a row's on the left edge. It
 * shows only when the pointer is there, and opens a menu (add, freeze,
 * delete). Not in the Tab order.
 *
 * The band (`.trameria-table-handle-zone`) is a 12px hit area across the line
 * and is not painted (painting it hid the outline of the paragraph above). A
 * row's band takes no hits at all: it would stop the caret from landing in the
 * 6px left of the first column's text. Look in the CSS, position in `style`.
 */
function Handle({
  direction,
  label,
  open,
  hidden,
  style,
  onOpen,
}: {
  direction: "row" | "col";
  label: string;
  open: boolean;
  /** Slid under a frozen row or column (hidden by the CSS). */
  hidden: boolean;
  style: CSSProperties;
  onOpen: (anchor: DOMRect) => void;
}) {
  return (
    <div
      className={`trameria-table-handle-zone trameria-table-handle-zone-${direction}`}
      style={style}
      data-hidden={hidden ? "true" : undefined}
    >
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        tabIndex={-1}
        data-open={open ? "true" : undefined}
        className={`trameria-table-handle trameria-table-handle-${direction}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => onOpen(e.currentTarget.getBoundingClientRect())}
      >
        <Dots direction={direction} />
      </button>
    </div>
  );
}

export function TableOverlay({
  editor,
  getPos,
  node,
  rects,
  fontSizePx,
  onResize,
  onPreviewWidths,
  onPreviewSlack,
  onAutoScroll,
  onMenuOpenChange,
  scroll = { top: 0, left: 0 },
  viewport,
}: TableOverlayProps) {
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  /**
   * The held grip's position (px from the table's left). Drawn from the border
   * where it was grabbed plus what changed since, never from a re-measured
   * border plus the movement: a width change rewraps cells, the table's height
   * changes, the ResizeObserver delivers moved rectangles, and adding the
   * movement again made the line run away from the pointer at twice the speed.
   */
  const [preview, setPreview] = useState<{
    index: number;
    left: number;
  } | null>(null);
  const drag = useRef<{
    index: number;
    /** The table's drawn width when grabbed (px): what shrinks is filled with slack. */
    totalPx: number;
    /**
     * The body width when grabbed (= the container's visible width, px). The
     * table never gets narrower. Not re-measured while dragging (the slack
     * padding changes the container).
     */
    fillPx: number;
    startX: number;
    /** The container's scroll when grabbed; auto-scrolled distance adds to the width. */
    startScrollLeft: number;
    /** The last pointer x (auto-scroll goes on while the pointer rests). */
    lastX: number;
    /** The container's left edge (screen), for the edge bands. */
    boxLeft: number;
    widths: number[];
    pointerId: number;
  } | null>(null);
  const autoFrame = useRef<number | null>(null);

  const { rows, cols, table } = rects;
  const x = (left: number) => left - table.left;
  const y = (top: number) => top - table.top;

  /**
   * Puts the focus back into the table (closing the menu, right after adding
   * a row or column). The focus goes back to the body before a floating
   * part goes away, or it falls to <body>.
   */
  const focusCell = (row: number, col: number) => {
    const pos = getPos();
    // Without that cell (just removed), only return to the body.
    if (pos !== undefined) selectCell(editor, pos, row, col);
    editor.commands.focus();
  };

  /**
   * Focus back to the editor with the caret right after (default) or before
   * the table. Used after removing a row or column: removing the last one
   * removes the table, so the caret cannot go back inside.
   */
  const focusOutside = (where: "before" | "after" = "after") => {
    const pos = getPos();
    if (pos === undefined) return;
    const { state, view } = editor;
    const at = state.doc.nodeAt(pos);
    // With the table gone, `pos` is already the next block: put it before that.
    const size = at?.type.name === "table" ? at.nodeSize : 0;
    const edge =
      where === "after"
        ? Math.min(pos + size, state.doc.content.size)
        : Math.max(pos, 0);
    const bias = where === "after" ? 1 : -1;
    let tr = state.tr;
    let selection = Selection.near(tr.doc.resolve(edge), bias);
    // A table at the document's edge has nowhere to put the caret: near comes
    // back into the table. Add an empty paragraph next to it.
    if (selection.from === pos && size > 0) {
      const paragraph = state.schema.nodes.paragraph?.createAndFill();
      if (!paragraph) return;
      tr = tr.insert(edge, paragraph);
      selection = Selection.near(tr.doc.resolve(edge + 1), 1);
    }
    view.dispatch(tr.setSelection(selection));
    view.focus();
  };

  /** Selects the cell, then runs a table command between undo boundaries. */
  const runAtCell = (row: number, col: number, run: () => void) => {
    const pos = getPos();
    if (pos === undefined || !selectCell(editor, pos, row, col)) return;
    separateUndo(editor);
    run();
    separateUndo(editor);
  };

  const addRow = (row: number, where: "above" | "below") => {
    setMenu(null);
    runAtCell(row, 0, () => {
      if (where === "above") editor.commands.addRowBefore();
      else editor.commands.addRowAfter();
    });
    // Into the new row's first cell (where typing starts).
    focusCell(where === "above" ? row : row + 1, 0);
  };

  /**
   * Adds a column. `widths` are fixed in the same transaction (in another one,
   * "a column more, a width short" would stay as an undo step). The new
   * column gets its neighbour's width.
   */
  const addColumn = (col: number, where: "left" | "right") => {
    setMenu(null);
    const pos = getPos();
    if (pos === undefined) return;
    runAtCell(0, col, () => {
      const chain = editor.chain();
      const added =
        where === "left" ? chain.addColumnBefore() : chain.addColumnAfter();
      added
        .command(({ tr }) => {
          const at = tr.doc.nodeAt(pos);
          const widths = at?.attrs.widths as number[] | null | undefined;
          if (!at || !widths) return true;
          const next = [...widths];
          next.splice(
            where === "left" ? col : col + 1,
            0,
            widths[col] ?? DEFAULT_COLUMN_EM,
          );
          tr.setNodeMarkup(pos, undefined, { ...at.attrs, widths: next });
          return true;
        })
        .run();
    });
    // Into the new column's header cell.
    focusCell(0, where === "left" ? col : col + 1);
  };

  const removeRow = (row: number) => {
    setMenu(null);
    runAtCell(row, 0, () => {
      editor.commands.deleteRow();
    });
    focusOutside("after");
  };

  const removeColumn = (col: number) => {
    setMenu(null);
    const pos = getPos();
    if (pos === undefined) return;
    runAtCell(0, col, () => {
      editor
        .chain()
        .deleteColumn()
        .command(({ tr }) => {
          const at = tr.doc.nodeAt(pos);
          // Removing the last column removes the table: nothing to fix.
          if (at?.type.name !== "table") return true;
          const widths = at.attrs.widths as number[] | null | undefined;
          if (!widths) return true;
          const next = [...widths];
          next.splice(col, 1);
          tr.setNodeMarkup(pos, undefined, {
            ...at.attrs,
            widths: next.length > 0 ? next : null,
          });
          return true;
        })
        .run();
    });
    focusOutside("after");
  };

  /**
   * Writes the frozen rows / columns (the option line's `fix-rows` /
   * `fix-cols`, 0-2), between undo boundaries like the widths, so one undo
   * takes it back.
   */
  const setFix = (kind: "row" | "col", index: number, value: number) => {
    setMenu(null);
    const pos = getPos();
    if (pos !== undefined) {
      // The boundary first: `separateUndo` may dispatch an empty transaction,
      // so the state is read after it (reading before throws "Applying a
      // mismatched transaction").
      separateUndo(editor);
      const { state, view } = editor;
      const at = state.doc.nodeAt(pos);
      if (at?.type.name === "table") {
        const key = kind === "row" ? "fixRows" : "fixCols";
        view.dispatch(
          state.tr.setNodeMarkup(pos, undefined, { ...at.attrs, [key]: value }),
        );
      }
      separateUndo(editor);
    }
    // The focus is in the menu; return it to the body before it goes.
    if (kind === "row") focusCell(index, 0);
    else focusCell(0, index);
  };

  // The container's size; without it, the table's own (a table that does not scroll).
  const clientWidth = viewport?.clientWidth ?? table.width;
  const clientHeight = viewport?.clientHeight ?? table.height;
  const offsetWidth = viewport?.offsetWidth ?? table.width;
  const offsetHeight = viewport?.offsetHeight ?? table.height;

  // Dragging a grip. Every pointerdown starts from the measured widths (not the
  // stored ones): a table without widths, one stretched to the body, or one
  // after the text size changed keeps its look on release (what is written is
  // what is shown).
  const startResize =
    (index: number) => (e: ReactPointerEvent<HTMLButtonElement>) => {
      const measured = widthsFromMeasured(
        cols.map((c) => c.width),
        fontSizePx,
      );
      const col = cols[index];
      if (!col || measured.length <= index) return;
      const left = x(col.right);
      // The container's box is the overlay's box (the node view lays one over
      // the other). happy-dom has no rectangles: 0, so no edge band.
      const box = e.currentTarget
        .closest(".trameria-table-overlay")
        ?.getBoundingClientRect() ?? { left: 0 };
      drag.current = {
        index,
        totalPx: displayedTotalPx(measured, fontSizePx, clientWidth),
        fillPx: clientWidth,
        startX: e.clientX,
        startScrollLeft: onAutoScroll?.(0) ?? 0,
        lastX: e.clientX,
        boxLeft: box.left,
        widths: measured,
        pointerId: e.pointerId,
      };
      // Not in happy-dom. In browsers, events keep coming outside the button.
      if (typeof e.currentTarget.setPointerCapture === "function") {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      setPreview({ index, left });
    };
  type Drag = NonNullable<typeof drag.current>;
  /**
   * The widths for the current drag position (stopped at the minimum). The
   * movement is the pointer's plus what auto-scroll moved (so columns scrolled
   * into view can be grabbed too).
   */
  const draggedWidths = (d: Drag, clientX: number, scrollLeft: number) =>
    resizeColumn(
      d.widths,
      d.index,
      pxToEm(clientX - d.startX + (scrollLeft - d.startScrollLeft), fontSizePx),
      d.fillPx / fontSizePx,
    );
  /** Shows the current widths (the document is not touched). */
  const applyDrag = (d: Drag, clientX: number, scrollLeft: number) => {
    const next = draggedWidths(d, clientX, scrollLeft);
    onPreviewWidths?.(next);
    // Slack for what shrank, so the scroll range does not shrink. 0 when
    // widening or trading width.
    onPreviewSlack?.(
      Math.max(0, d.totalPx - displayedTotalPx(next, fontSizePx, d.fillPx)),
    );
    // The grip goes where the border will be drawn with these widths (the
    // same spreading of what the body has left), so nothing jumps on release.
    // Rectangles keep updating while dragging (rewrapped rows), so the
    // movement is never added to a measured border (it would count twice).
    setPreview({
      index: d.index,
      left: columnEdgePx(next, d.index, fontSizePx, d.fillPx),
    });
  };
  /**
   * How far to scroll per frame inside an edge band (px); 0 outside one or
   * toward an end that cannot scroll further. Faster the further out (half the
   * overshoot, within 2-16px).
   */
  const autoStep = (d: Drag, scrollLeft: number) => {
    const right = d.boxLeft + clientWidth;
    const speed = (over: number) =>
      Math.min(Math.max(over / 2, AUTO_MIN), AUTO_MAX);
    const overRight = d.lastX - (right - AUTO_EDGE);
    if (overRight > 0) return speed(overRight);
    const overLeft = d.boxLeft + AUTO_EDGE - d.lastX;
    // Left stops at the start; right goes on (the table widens).
    if (overLeft > 0 && scrollLeft > 0) return -speed(overLeft);
    return 0;
  };
  const stopAuto = () => cancelFrame(autoFrame);
  const runAuto = () => {
    autoFrame.current = null;
    const d = drag.current;
    if (!d) return;
    const step = autoStep(d, onAutoScroll?.(0) ?? d.startScrollLeft);
    if (step === 0) return;
    // Scroll first, then size from there (the wider table lets the next frame scroll further).
    const scrollLeft = onAutoScroll?.(step) ?? d.startScrollLeft;
    applyDrag(d, d.lastX, scrollLeft);
    if (typeof requestAnimationFrame === "function") {
      autoFrame.current = requestAnimationFrame(runAuto);
    }
  };
  const startAuto = () => {
    if (autoFrame.current !== null) return;
    // Nothing to scroll (an overlay without a node view): looping would only
    // recompute widths every frame.
    if (!onAutoScroll) return;
    if (typeof requestAnimationFrame !== "function") return;
    autoFrame.current = requestAnimationFrame(runAuto);
  };
  const moveResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    d.lastX = e.clientX;
    const scrollLeft = onAutoScroll?.(0) ?? d.startScrollLeft;
    applyDrag(d, e.clientX, scrollLeft);
    if (autoStep(d, scrollLeft) !== 0) startAuto();
    else stopAuto();
  };
  const endResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    stopAuto();
    const scrollLeft = onAutoScroll?.(0) ?? d.startScrollLeft;
    drag.current = null;
    setPreview(null);
    onPreviewWidths?.(null);
    onPreviewSlack?.(null);
    // Someone added or removed a column meanwhile: the index no longer fits.
    const widths = node.attrs.widths as number[] | null;
    if (widths && widths.length !== d.widths.length) return;
    const final = draggedWidths(d, e.clientX, scrollLeft);
    // Pressed without moving: no write, no undo step.
    if (final[d.index] === d.widths[d.index]) return;
    separateUndo(editor);
    onResize(final);
    separateUndo(editor);
  };
  const cancelResize = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    stopAuto();
    drag.current = null;
    setPreview(null);
    onPreviewWidths?.(null);
    onPreviewSlack?.(null);
  };

  // Escape while holding a grip cancels and restores the widths. Escape does
  // not cause `pointercancel`, so it is listened to here (and since
  // `drag.current` is cleared, the later pointerup passes by). Captured and
  // stopped: otherwise the editor's own Escape handling would take it and
  // leave the document as well.
  const dragging = preview !== null;
  // frontend-development: not a fetch; Escape while a grip is held.
  useEffect(() => {
    if (!dragging) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      cancelFrame(autoFrame);
      drag.current = null;
      setPreview(null);
      onPreviewWidths?.(null);
      onPreviewSlack?.(null);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [dragging, onPreviewWidths, onPreviewSlack]);

  // frontend-development: not a fetch; no animation frame outlives the overlay.
  useEffect(() => () => cancelFrame(autoFrame), []);

  // Tells the node view the menu's state (it raises this table's layer while
  // open). No cleanup: the mark sits on `div.trameria-table`,
  // which goes away with the overlay.
  const menuOpen = menu !== null;
  // frontend-development: not a fetch; reports the menu's state to the node view.
  useEffect(() => {
    onMenuOpenChange?.(menuOpen);
  }, [menuOpen, onMenuOpenChange]);

  // The last row / column come from the node (the rectangles may be older).
  const lastRow = Math.max(node.childCount - 1, 0);
  const lastCol = Math.max((node.maybeChild(0)?.childCount ?? 1) - 1, 0);

  // Frozen rows / columns (0-2) and their bottom / right edge (px from the
  // table's top left). The overlay does not scroll, so only what is not frozen
  // moves with the content (minus `scroll`); frozen ones stay where their
  // sticky cells are. Handles that slid under them are hidden - left shown,
  // handles of rows out of sight lined up next to the header.
  const fixRows = Number(node.attrs.fixRows ?? 0);
  const fixCols = Number(node.attrs.fixCols ?? 0);
  const fixedBottom =
    fixRows > 0 && rows[fixRows - 1] ? y(rows[fixRows - 1].bottom) : 0;
  const fixedRight =
    fixCols > 0 && cols[fixCols - 1] ? x(cols[fixCols - 1].right) : 0;

  // The table's visible width. Strips, grip windows and "+" follow the table,
  // not the container: a table narrower than the container uses its own
  // width; a wider one the container's outer width (scrollbar included, so
  // the strip sits outside it). Following the container left the "+" of a
  // narrow table floating at the body's right edge, away from the table.
  const narrow = table.width < clientWidth - 0.5;
  const shownWidth = narrow ? table.width : offsetWidth;
  const windowWidth = Math.min(table.width, clientWidth);

  const rowItems = (index: number): FloatingMenuItem[] => [
    // No "add above" on the header row: a GFM table's first row is its header,
    // so adding above would demote the header to a plain row.
    ...(index === 0
      ? []
      : [
          {
            label: m.blocks_table_add_above(),
            onSelect: () => addRow(index, "above"),
          },
        ]),
    {
      label: m.blocks_table_add_below(),
      onSelect: () => addRow(index, "below"),
    },
    // Only rows 1-2 can be frozen (fix-rows 0-2). Not shown when already
    // frozen there (it would change nothing and still add an undo step).
    ...(index <= 1 && node.attrs.fixRows !== index + 1
      ? [
          {
            label: m.blocks_table_freeze_here(),
            onSelect: () => setFix("row", index, index + 1),
          },
        ]
      : []),
    ...(node.attrs.fixRows > 0
      ? [
          {
            label: m.blocks_table_unfreeze(),
            onSelect: () => setFix("row", index, 0),
          },
        ]
      : []),
    {
      // Next to the block menu's "delete" (the whole table), so the words say
      // what goes.
      label: m.blocks_table_delete_row(),
      danger: true,
      onSelect: () => removeRow(index),
    },
  ];
  const colItems = (index: number): FloatingMenuItem[] => [
    {
      label: m.blocks_table_add_left(),
      onSelect: () => addColumn(index, "left"),
    },
    {
      label: m.blocks_table_add_right(),
      onSelect: () => addColumn(index, "right"),
    },
    ...(index <= 1 && node.attrs.fixCols !== index + 1
      ? [
          {
            label: m.blocks_table_freeze_here(),
            onSelect: () => setFix("col", index, index + 1),
          },
        ]
      : []),
    ...(node.attrs.fixCols > 0
      ? [
          {
            label: m.blocks_table_unfreeze(),
            onSelect: () => setFix("col", index, 0),
          },
        ]
      : []),
    {
      label: m.blocks_table_delete_col(),
      danger: true,
      onSelect: () => removeColumn(index),
    },
  ];

  return (
    <>
      {/* The column handles' band: a 12px window across the container's top
          edge; handles outside it are clipped (the overlay is outside the
          container, so the window does not scroll). */}
      <div
        className="trameria-table-band-top"
        style={{ left: 0, top: -BAND_HALF, width: windowWidth, height: BAND }}
      >
        {cols.map((col, index) => (
          <Handle
            // biome-ignore lint/suspicious/noArrayIndexKey: a column is its position (cells have no id)
            key={`col-${index}`}
            direction="col"
            label={m.blocks_table_col_actions({ n: index + 1 })}
            open={menu?.kind === "col" && menu.index === index}
            hidden={index >= fixCols && x(col.left) - scroll.left < fixedRight}
            style={{
              left: x(col.left) - (index < fixCols ? 0 : scroll.left),
              top: 0,
              width: col.width,
              height: BAND,
            }}
            onOpen={(anchor) => setMenu({ kind: "col", index, anchor })}
          />
        ))}
      </div>
      {/* The row handles' band (a 12px window across the left edge). */}
      <div
        className="trameria-table-band-left"
        style={{ left: -BAND_HALF, top: 0, width: BAND, height: clientHeight }}
      >
        {rows.map((row, index) => (
          <Handle
            // biome-ignore lint/suspicious/noArrayIndexKey: a row is its position (rows have no id)
            key={`row-${index}`}
            direction="row"
            label={m.blocks_table_row_actions({ n: index + 1 })}
            open={menu?.kind === "row" && menu.index === index}
            hidden={index >= fixRows && y(row.top) - scroll.top < fixedBottom}
            style={{
              top: y(row.top) - (index < fixRows ? 0 : scroll.top),
              left: 0,
              width: BAND,
              height: row.height,
            }}
            onOpen={(anchor) => setMenu({ kind: "row", index, anchor })}
          />
        ))}
      </div>
      {/* The width grips. They move with the content, clipped to the visible
          table, widened by half a grip (4px): in a table as wide as the body
          the last grip straddles the container's right edge, and clipping
          exactly would halve its hit area (8 -> 4px) and its hover line - for
          the only way to change the table's whole width. The right "+" strip
          starts 4px past the table's edge, so they do not overlap. */}
      <div
        className="trameria-table-grips"
        style={{
          left: 0,
          top: 0,
          width: windowWidth + GRIP / 2,
          height: clientHeight,
        }}
      >
        {cols.map((col, index) => {
          // A frozen column's grip is treated like the column: not scrolled
          // and never hidden (hiding grips under frozen columns once hid the
          // frozen column's own border too).
          const fixed = index < fixCols;
          return (
            // A BUTTON, so the node view's `stopEvent` keeps it from PM; PM
            // ignores pointer events, so they can be captured.
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: a border is its position
              key={`grip-${index}`}
              type="button"
              aria-label={m.blocks_table_col_resize({ n: index + 1 })}
              tabIndex={-1}
              className="trameria-table-resize"
              data-active={preview?.index === index ? "true" : undefined}
              // Hidden when it slid under a frozen column (no col-resize
              // cursor and accent line over the frozen column). Not while held.
              data-hidden={
                !fixed &&
                preview?.index !== index &&
                x(col.right) - scroll.left < fixedRight
                  ? "true"
                  : undefined
              }
              style={{
                // While held, counted from where it was grabbed (re-measured
                // rectangles are not added again).
                left:
                  (preview?.index === index ? preview.left : x(col.right)) -
                  (fixed ? 0 : scroll.left) -
                  GRIP / 2,
                top: -scroll.top,
                width: GRIP,
                height: table.height,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onPointerDown={startResize(index)}
              onPointerMove={moveResize}
              onPointerUp={endResize}
              onPointerCancel={cancelResize}
              // The capture was taken away (another pointer, the element went):
              // cancel too. A normal release also ends here, after pointerup
              // cleared `drag.current`, so it passes by.
              onLostPointerCapture={cancelResize}
            />
          );
        })}
      </div>
      {/* The "+" strips sit outside the table (below and right), measured from
          the table's visible width, so they stay in place without scrolling.
          4px apart from the table. Not on touch devices (CSS). */}
      <AddStrip
        label={m.blocks_table_add_row()}
        style={{
          left: 0,
          top: offsetHeight + STRIP_GAP,
          width: shownWidth,
          height: STRIP,
        }}
        onClick={() => addRow(lastRow, "below")}
      />
      <AddStrip
        label={m.blocks_table_add_col()}
        style={{
          left: shownWidth + STRIP_GAP,
          top: 0,
          width: RIGHT_STRIP,
          height: Math.max(offsetHeight, 24),
        }}
        // The strip is 12px, so the "+" stays within 12px (13px sticks out 0.5px each side).
        iconSize={11}
        onClick={() => addColumn(lastCol, "right")}
      />
      {menu && (
        <FloatingMenu
          anchor={menu.anchor}
          placement={menu.kind === "row" ? "right" : "below"}
          label={
            menu.kind === "row"
              ? m.blocks_table_row_actions({ n: menu.index + 1 })
              : m.blocks_table_col_actions({ n: menu.index + 1 })
          }
          items={
            menu.kind === "row" ? rowItems(menu.index) : colItems(menu.index)
          }
          onClose={() => {
            const open = menu;
            setMenu(null);
            // The focus is in the menu's area; return it to that row's /
            // column's cell before it goes.
            if (open.kind === "row") focusCell(open.index, 0);
            else focusCell(0, open.index);
          }}
        />
      )}
    </>
  );
}
